import styled from '@emotion/styled';
import React, { SyntheticEvent } from 'react';
import { PriceProps, SummaryPrice } from '../Summary.definitions';
import { Button } from '~/shared/components/Button/Button';
import { Form } from '~/shared/components/form/components/form/form';
import { Checkbox } from '~/shared/components/form/components/form-fields/checkbox/checkbox';
import { useForm } from 'react-hook-form';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { useBasket } from '~/features/basket/hooks';
import { default as Icon } from '~/icons/checkmark.svg';
import { LoaderSpinner } from '~/features/checkout/components/DeliveryInformation/styled';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { usePaymentDetails } from '../../../../features/commerce-api/hooks/usePaymentDetails';
import { useValidateQuantity } from '~/features/basket/hooks/useValidateQuantity';
import { theme } from '~/theme';
import { Spacer } from '~/theme/design-system/components/Spacer';
import Typography from '../../Typography/Typography';
import styles from '../Summary.module.scss';
import clsx from 'clsx';
import { useLoginDrawer } from '~/features/login/hooks';
import { LoginDrawerMode } from '~/features/login/hooks/useLoginDrawerState';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useTotalBonusEarning } from '~/services/special-price';

const Price = (price: SummaryPrice) => {
    return (
        <div className={clsx(styles.priceWrapper, price.type === 'discount' && styles.discount)}>
            <div>
                <Typography as="p" variant="paragraph-S">
                    {price.label}
                </Typography>
                {price.info && (
                    <Typography as="p" variant="disclaimer-S">
                        {price.info}
                    </Typography>
                )}
            </div>
            <Typography as="p" variant="paragraph-S">
                {price.value ?? 0}
            </Typography>
        </div>
    );
};

const CTA = styled(
    (
        props: Pick<PriceProps, 'ctaLabel'> & {
            className?: string;
            onClick?: (e: SyntheticEvent) => void;
            disabledButton?: boolean;
            loading?: boolean;
        },
    ) => (
        <div className={props.className}>
            <Button
                data-testid="go-to-checkout"
                size="large"
                variant="pdp"
                onClick={props.onClick}
                disabled={props.disabledButton}
            >
                {props.loading ? <LoaderSpinner /> : props.ctaLabel}
            </Button>
        </div>
    ),
)(({ theme }) => ({
    paddingTop: theme.space[4],
}));

export const PriceSection = styled(
    ({
        ctaLabel,
        onCtaHandler,
        signupName,
        onSignupIntent,
        signupLabel,
        disabledButton,
        fullMode = false,
        isRegistered,
        freeShipping = false,
        amountToFreeShipping,
        setMembership,
        membership,
        isLoading,
        children,
    }: PriceProps) => {
        const methods = useForm();
        const basket = useBasket();
        const { translate } = useTranslation();
        const { convertPriceToLocale } = useUtils();
        const { changingCarrier } = useCheckoutState();
        const { isPlusMember, isStorePersonnel } = useCustomer();
        const { totalBonusEarning, hasBonusEarning } = useTotalBonusEarning();
        const { open } = useLoginDrawer();

        const isFetching = false;
        const { validateQuantity, isValidating } = useValidateQuantity();

        const selectMemberShip = () => {
            const newMembershipStatus = !membership;
            setMembership(newMembershipStatus);
            localStorage.setItem('membershipStatus', JSON.stringify(newMembershipStatus));
        };

        const handleClick = async (e: SyntheticEvent) => {
            /**
             * Here we could get the value for the signupIntent checbox,
             * and pass it to the onCtaHandler() callback if needed.
             * Can't get the value out of the checkbox though.
             * But we need to check first if the stock is still valid
             */
            const isInstocks = await validateQuantity();

            if (onCtaHandler && isInstocks) {
                e.preventDefault();
                onCtaHandler();
            }
        };

        const { basePrice, remainingTotal, discountPrice, giftcardUsage, bonusRewardTotals } =
            usePaymentDetails(basket, membership);

        return (
            <section>
                <Price
                    label={translate('checkout.priceSection.price')}
                    value={convertPriceToLocale(basePrice)}
                    type="default"
                />
                {basket?.couponItems?.map(({ code }) => (
                    <Price
                        key={code}
                        label={translate('checkout.promoSection.discountCodeLabel')}
                        value={'-' + code}
                        type="discount"
                    />
                ))}
                {!!discountPrice && (
                    <Price
                        label={translate('checkout.priceSection.discount')}
                        value={'-' + convertPriceToLocale(discountPrice)}
                        type="discount"
                    />
                )}
                <Price
                    label={translate('checkout.priceSection.shipping')}
                    value={convertPriceToLocale(basket.shippingTotal)}
                    type="default"
                />
                {!freeShipping && amountToFreeShipping && basket.c_freeShippingAllowed ? (
                    <StyledFreeShip role="status">
                        {translate('basket.basket.forFreeShipping.summary', {
                            amount: String(amountToFreeShipping),
                        })}
                    </StyledFreeShip>
                ) : freeShipping && basket.c_freeShippingAllowed ? (
                    <StyledFreeShip>
                        {translate('basket.basket.freeShipping.summary')}
                    </StyledFreeShip>
                ) : null}
                {!!giftcardUsage && (
                    <Price
                        label={translate('checkout.giftCertificateForm.cardName')}
                        value={'-' + convertPriceToLocale(giftcardUsage)}
                        type="discount"
                    />
                )}
                {bonusRewardTotals.quantity > 0 && (
                    <Price
                        label={`${translate('basket.bonusRewardsAmount', { amount: bonusRewardTotals.quantity.toString() })}`}
                        value={'-' + convertPriceToLocale(bonusRewardTotals?.amount)}
                        type="discount"
                    />
                )}
                <Price
                    label={translate('basket.priceWithTax')} // order_total
                    value={convertPriceToLocale(remainingTotal)}
                    type="default"
                />
                <Spacer space={theme.space[4]} />
                {fullMode && !isRegistered && (
                    <>
                        <StyledMemberBonus>
                            <StyledCheckLabel>
                                <StyledCheckboxElement>
                                    <StyledCheckbox
                                        checked={membership}
                                        onChange={() => selectMemberShip()}
                                        type="checkbox"
                                        aria-label={`${translate('basket.becomeMember')} checkbox`}
                                    />
                                    <StyledCheckboxIcon color="white" size={12} />
                                </StyledCheckboxElement>
                            </StyledCheckLabel>
                            <div>
                                <StyleBecomeMember>
                                    {translate('basket.becomeMember')}{' '}
                                    {!!basket.c_totalMemberDiscount && '— '}
                                    <span>
                                        {!!basket.c_totalMemberDiscount &&
                                            translate('basket.saveTotal', {
                                                amount: convertPriceToLocale(
                                                    Number(basket.c_totalMemberDiscount),
                                                ),
                                            })}
                                    </span>
                                </StyleBecomeMember>
                            </div>
                        </StyledMemberBonus>
                        <StyledMemberBonus>
                            <StyleWinBonus>
                                {hasBonusEarning &&
                                    translate('basket.winBonus', {
                                        amount: convertPriceToLocale(totalBonusEarning),
                                    })}
                            </StyleWinBonus>
                        </StyledMemberBonus>
                    </>
                )}
                {onSignupIntent && signupName && signupLabel && (
                    <Form methods={methods} onSubmit={onSignupIntent}>
                        <Checkbox
                            label={
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: signupLabel,
                                    }}
                                />
                            }
                            name={signupName}
                        />
                    </Form>
                )}

                {children}

                {ctaLabel && (
                    <CTA
                        ctaLabel={ctaLabel}
                        onClick={handleClick}
                        disabledButton={disabledButton || isValidating}
                        loading={changingCarrier || isFetching || isValidating || isLoading}
                    />
                )}

                {!isPlusMember && !isStorePersonnel ? (
                    <div className={styles.loginWrapper}>
                        <Typography as="p" variant="paragraph-S">
                            {translate('basket.alreadyMember')}
                        </Typography>
                        <button onClick={() => open(LoginDrawerMode.LOGIN)}>
                            <Typography as="span" variant="paragraph-S" weight="bold">
                                {translate('header.login')}
                            </Typography>
                        </button>
                    </div>
                ) : null}
            </section>
        );
    },
)({});

export const StyledCheckLabel = styled.label(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: `${theme.fontSizes['xs']}`,
    lineHeight: `${theme.fontSizes['xl']}`,
    maxHeight: `${theme.fontSizes['xl']}`,
    cursor: 'pointer',
    [`input:checked`]: {
        width: '100%',
        cursor: 'inherit',
        height: '100%',
        margin: 0,
        opacity: 0,
        padding: 0,
    },
}));

export const StyledCheckbox = styled.input(({ theme }) => ({
    padding: `2px 0 0 ${theme.space[2]}`,
    margin: 0,
    height: '24px',
    width: '24px',
    color: theme.colors.black,
    ...theme.mixins.useTextStyle('bodySmall'),
}));

export const StyledCheckboxElement = styled.div(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.sizes.cornerRadius,
    backgroundColor: theme.colors.black,
    boxSizing: 'border-box',
    width: 24,
    height: 24,
    cursor: 'pointer',
    marginRight: `${theme.space[2]}`,
    svg: {
        margin: 0,
    },
    ':has(:focus-visible)': {
        boxShadow: theme.shadows.accessibility,
    },
}));

export const StyledCheckboxIcon = styled(Icon)({
    display: 'block',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
});

export const StyleBecomeMember = styled.p(({ theme }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes['2xs'],
    span: {
        color: theme.colors.redMediumLight,
    },
}));
export const StyleWinBonus = styled.p(({ theme }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes['2xs'],
    paddingLeft: theme.space[5],
}));
export const StyledFreeShip = styled.p(({ theme }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes['exs'],
    marginBottom: theme.space[2],
}));

export const StyledMemberBonus = styled.div(() => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
}));
